import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Inbox, Loader2 } from 'lucide-react'
import { Button } from '@diversioteam/diversio-ds'

import { NotificationItem } from '../NotificationItem'
import { LoadMoreNotificationCard } from '../LoadMoreNotificationCard'

import * as S from './NotificationDropdown.styles'
import type { NotificationDropdownProps, NotificationDropdownState } from './NotificationDropdown.types'

import { notificationConfig } from '@/features/notifications'
import { UI_PAGE_SIZE } from '@/api/actions/notifications/notificationConstants'

const USER_TYPE_CONFIG = {
    manager: { emptyTitle: 'No notifications' },
    hr: { emptyTitle: 'No notifications' },
    employee: { emptyTitle: 'No notifications' },
} as const

export function NotificationDropdown({
    notifications,
    userType,
    isOpen,
    isLoading = false,
    isDismissingAll = false,
    totalNotifications,
    hasMoreNotifications,
    isLoadingMore,
    onLoadMore,
    onClose: _onClose,
    onNotificationClick,
    onNotificationDismiss,
    onClearAll,
    lastOpenedAt,
    onAcknowledgeNew,
}: NotificationDropdownProps) {
    const [state, setState] = useState<NotificationDropdownState>({
        filteredNotifications: [],
    })

    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const scrollPositionRef = useRef<number>(0)
    const wasLoadingMoreRef = useRef<boolean>(false)

    const processedNotifications = useMemo(() => {
        return notifications.filter((n) => !n.is_dismissed)
    }, [notifications])

    useEffect(() => {
        setState((prev) => ({
            ...prev,
            filteredNotifications: processedNotifications,
        }))
    }, [processedNotifications])

    useEffect(() => {
        if (wasLoadingMoreRef.current && !isLoadingMore && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollPositionRef.current
            wasLoadingMoreRef.current = false
        }
    }, [isLoadingMore])

    const handleNotificationListScroll = useCallback((event: React.WheelEvent) => {
        const target = event.currentTarget
        const { scrollTop, scrollHeight, clientHeight } = target

        if (event.deltaY > 0 && scrollTop + clientHeight >= scrollHeight - 1) {
            event.preventDefault()
            event.stopPropagation()
        } else if (event.deltaY < 0 && scrollTop <= 0) {
            event.preventDefault()
            event.stopPropagation()
        }
    }, [])

    const handleLoadMore = useCallback(() => {
        if (scrollContainerRef.current && !isLoadingMore) {
            scrollPositionRef.current = scrollContainerRef.current.scrollTop
            wasLoadingMoreRef.current = true
            onLoadMore()
        }
    }, [onLoadMore, isLoadingMore])

    const visibleNotifications = state.filteredNotifications

    const userConfig = USER_TYPE_CONFIG[userType]
    // icon is not used in dropdown UI currently
    const isEmpty = state.filteredNotifications.length === 0
    const newCount = useMemo(() => {
        if (!lastOpenedAt) return 0
        try {
            return visibleNotifications.filter((n) => new Date(n.created_at).getTime() > lastOpenedAt).length
        } catch {
            return 0
        }
    }, [visibleNotifications, lastOpenedAt])

    return (
        <AnimatePresence>
            {isOpen && (
                <S.DropdownContainer
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    $isOpen={isOpen}
                    role="dialog"
                    aria-labelledby="notifications-title"
                    aria-modal={false}
                >
                    <S.DropdownHeader>
                        <S.HeaderTitle>
                            <div id="notifications-title">Notifications</div>
                            <S.HeaderActions>
                                {totalNotifications > 0 && (
                                    <S.HeaderButton
                                        onClick={onClearAll}
                                        disabled={isDismissingAll}
                                        title={
                                            isDismissingAll
                                                ? 'Dismissing notifications...'
                                                : 'Dismiss all notifications'
                                        }
                                    >
                                        {isDismissingAll && <Loader2 size={14} className="animate-spin" />}
                                        {isDismissingAll ? 'Dismissing...' : 'Dismiss all'}
                                    </S.HeaderButton>
                                )}
                            </S.HeaderActions>
                        </S.HeaderTitle>
                    </S.DropdownHeader>

                    <S.NotificationList
                        ref={scrollContainerRef}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        $isEmpty={isEmpty || isLoading}
                        $maxHeight={notificationConfig.dropdown.maxHeight}
                        onWheel={handleNotificationListScroll}
                    >
                        {isLoading ? (
                            <S.LoadingState>
                                <S.LoadingSpinner
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                />
                                Loading notifications...
                            </S.LoadingState>
                        ) : isEmpty ? (
                            <S.EmptyState $userType={userType}>
                                <S.EmptyStateIcon>
                                    <Inbox size={24} />
                                </S.EmptyStateIcon>
                                <S.EmptyStateTitle>{userConfig.emptyTitle}</S.EmptyStateTitle>
                            </S.EmptyState>
                        ) : (
                            <>
                                {newCount > 0 && (
                                    <S.StickyNewPill>
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                                if (scrollContainerRef.current) {
                                                    scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
                                                }
                                                onAcknowledgeNew?.()
                                            }}
                                        >
                                            {newCount} new — Jump to top
                                        </Button>
                                    </S.StickyNewPill>
                                )}
                                <AnimatePresence mode="popLayout">
                                    {visibleNotifications.map((notification, index) => (
                                        <NotificationItem
                                            key={notification.id}
                                            notification={notification}
                                            onClick={onNotificationClick}
                                            onDismiss={onNotificationDismiss}
                                            isFirst={index === 0}
                                            isLast={!hasMoreNotifications && index === visibleNotifications.length - 1}
                                            animationIndex={index}
                                            isNew={
                                                Boolean(lastOpenedAt) &&
                                                new Date(notification.created_at).getTime() > (lastOpenedAt || 0)
                                            }
                                        />
                                    ))}
                                </AnimatePresence>

                                {hasMoreNotifications && (
                                    <LoadMoreNotificationCard
                                        remainingCount={Math.min(
                                            UI_PAGE_SIZE,
                                            totalNotifications - visibleNotifications.length,
                                        )}
                                        onLoadMore={handleLoadMore}
                                        isLoading={isLoadingMore}
                                    />
                                )}
                            </>
                        )}
                    </S.NotificationList>
                </S.DropdownContainer>
            )}
        </AnimatePresence>
    )
}

NotificationDropdown.displayName = 'NotificationDropdown'
